import { useEffect, useMemo, useState } from "react";
import { IoClose } from "react-icons/io5";
import { getGalleryCategoryImages, NO_IMAGE_URL } from "./utils/packageGallery";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import styles from "./GalleryModal.module.css";

const MODAL_TABS = [
  { id: "all", label: "All Images" },
  { id: "activity", label: "Activity" },
  { id: "property", label: "Property" },
  { id: "clientReview", label: "Client Review" },
];

export default function GalleryModal({
  open,
  onClose,
  title = "",
  categories = {},
  initialCategory = "all",
  initialImageSrc = "",
  hideCategoryTabs = false,
}) {
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [activeIndex, setActiveIndex] = useState(0);

  useBodyScrollLock(open);

  const categoryImages = useMemo(
    () => getGalleryCategoryImages(categories, activeCategory),
    [categories, activeCategory]
  );

  const activeImage = categoryImages[activeIndex] || categoryImages[0] || null;

  useEffect(() => {
    if (!open) return undefined;
    setActiveCategory(initialCategory);
    const images = getGalleryCategoryImages(categories, initialCategory);
    const index = images.findIndex((item) => item.src === initialImageSrc);
    setActiveIndex(index >= 0 ? index : 0);
  }, [open, initialCategory, initialImageSrc, categories]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className={styles.root} role="dialog" aria-modal="true" aria-label="Image gallery">
      <button type="button" className={styles.overlay} onClick={onClose} aria-label="Close gallery" />
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <IoClose />
          </button>
        </div>

        {!hideCategoryTabs ? (
          <>
            <div className={styles.tabs}>
              {MODAL_TABS.map((tab) => {
                const count = categories?.[tab.id]?.length || 0;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    className={`${styles.tab} ${activeCategory === tab.id ? styles.tabActive : ""}`}
                    onClick={() => {
                      setActiveCategory(tab.id);
                      setActiveIndex(0);
                    }}
                  >
                    {tab.label} ({count})
                  </button>
                );
              })}
            </div>
            <div className={styles.divider} />
          </>
        ) : null}

        {!categoryImages.length ? (
          <div className={styles.content}>
            <div className={styles.previewWrap}>
              <img
                src={NO_IMAGE_URL}
                alt="No preview available"
                className={styles.previewImg}
              />
            </div>
            <div className={styles.thumbsWrap}>
              <div className={styles.thumbsGrid}>
                <button type="button" className={`${styles.thumbBtn} ${styles.thumbBtnActive}`}>
                  <img src={NO_IMAGE_URL} alt="No preview available" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.content}>
            <div className={styles.previewWrap}>
              {activeImage ? (
                <img
                  src={activeImage.src}
                  alt={activeImage.alt || title}
                  className={styles.previewImg}
                  onError={(event) => {
                    if (event.currentTarget.src !== NO_IMAGE_URL) {
                      event.currentTarget.src = NO_IMAGE_URL;
                    }
                  }}
                />
              ) : null}
            </div>

            <div className={styles.thumbsWrap}>
              <div className={styles.thumbsGrid}>
                {categoryImages.map((image, index) => (
                  <button
                    key={image.id}
                    type="button"
                    className={`${styles.thumbBtn} ${
                      index === activeIndex ? styles.thumbBtnActive : ""
                    }`}
                    onClick={() => setActiveIndex(index)}
                    aria-label={`View image ${index + 1}`}
                  >
                    <img
                      src={image.src}
                      alt={image.alt || ""}
                      onError={(event) => {
                        if (event.currentTarget.src !== NO_IMAGE_URL) {
                          event.currentTarget.src = NO_IMAGE_URL;
                        }
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
